import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from '~/components/ConfirmDialog'
import Spinner from '~/components/Spinner'
import CertificateSettings from './CertificateSettings'
import CertificateTemplatesBar from './CertificateTemplatesBar'
import { useCertificateTemplates } from './use-certificate-templates'

type Pending = { run: () => Promise<unknown>, done: () => void }

/** Les modèles d'attestation (#162) : la liste, puis l'éditeur du modèle choisi. */
function CertificatesPage() {
  const { t } = useTranslation()
  const templates = useCertificateTemplates()
  const [dirty, setDirty] = useState(false)
  const [pending, setPending] = useState<Pending | null>(null)
  const onDirtyChange = useCallback((value: boolean) => setDirty(value), [])

  /**
   * Changer de modèle, en créer ou en dupliquer un recharge l'éditeur : avec
   * des modifications non enregistrées, l'admin confirme d'abord qu'il les perd.
   */
  const guard = <T,>(action: () => Promise<T> | T) => async (): Promise<T | false> => {
    if (!dirty) return action()
    return new Promise((resolve) => {
      setPending({ run: async () => resolve(await action()), done: () => resolve(false) })
    })
  }

  if (templates.error) {
    return <p role="alert" className="p-6 text-center text-sm text-destructive">{t('certificates.templates.loadError')}</p>
  }
  if (templates.loading && !templates.selected) {
    return (
      <div className="flex justify-center p-10">
        <Spinner className="size-6" />
      </div>
    )
  }

  const guarded = {
    ...templates,
    select: (id: string) => void guard(() => templates.select(id))(),
    create: async (name: string) => (await guard(() => templates.create(name))()) === true,
    duplicate: (id: string, name: string) => guard(() => templates.duplicate(id, name))(),
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <CertificateTemplatesBar templates={guarded} />
      {templates.selected && (
        // Clé = modèle : changer de modèle repart d'un formulaire neuf.
        <CertificateSettings
          key={templates.selected.id}
          templateId={templates.selected.id}
          onSaved={templates.refetch}
          onDirtyChange={onDirtyChange}
        />
      )}
      <ConfirmDialog
        open={pending != null}
        title={t('certificates.templates.discardTitle')}
        confirmLabel={t('certificates.templates.discard')}
        onCancel={() => {
          pending?.done()
          setPending(null)
        }}
        onConfirm={() => {
          void pending?.run()
          setPending(null)
        }}
      >
        {t('certificates.templates.discardBody')}
      </ConfirmDialog>
    </div>
  )
}

export default CertificatesPage
